import type { Contact, PlanShare, Share, SharedOperation, ShareInput } from '@fin/shared';
import { useState } from 'react';
import { post, put } from '../../lib/api.ts';
import { dateShort, money } from '../../lib/format.ts';
import { invalidate, invalidateMoney, useQuery } from '../../lib/query.ts';
import { Button, ConfirmButton, Icon, Input, NumberInput, Select, cx, toast, useAction } from '../../components/ui.tsx';

/** one row of the shares editor: an existing share keeps its debtId */
export interface ShareDraft { key: string; debtId?: number; contactId: number | ''; contactName: string; amount: number | null }

export const shareDrafts = (shares: Share[]): ShareDraft[] =>
  shares.map((s) => ({ key: String(s.debtId), debtId: s.debtId, contactId: s.contactId ?? '', contactName: s.contactId ? '' : s.contactName ?? '', amount: s.amount }));

/** planned parts of a plan line: the people are the same, there is no debt yet */
export const planShareDrafts = (shares: PlanShare[] | undefined): ShareDraft[] =>
  (shares ?? []).map((s, i) => ({ key: s.debtId ? String(s.debtId) : `plan-${i}`, debtId: s.debtId, contactId: s.contactId ?? '', contactName: s.contactId ? '' : s.contactName, amount: s.amount }));

/** shares a new operation starts with (from a shared plan line) */
export const inputDrafts = (shares: ShareInput[] | undefined): ShareDraft[] =>
  (shares ?? []).map((s, i) => ({ key: `new-${i}`, contactId: s.contactId ?? '', contactName: s.contactName ?? '', amount: s.amount }));

/** filled rows only; a row without a person or a sum is a row still being typed */
export function sharesOf(drafts: ShareDraft[]): ShareInput[] {
  return drafts.filter((d) => d.amount && (d.debtId || d.contactId || d.contactName.trim()))
    .map((d) => ({ debtId: d.debtId, contactId: d.contactId || undefined, contactName: d.contactId ? undefined : d.contactName.trim() || undefined, amount: d.amount! }));
}

export const sharesTotal = (drafts: ShareDraft[]) => sharesOf(drafts).reduce((s, x) => s + x.amount, 0);

/** «Доли других»: who of the people owes what part of the operation */
export function SharesEditor({ value, onChange, contacts, total, currency, settled }: {
  value: ShareDraft[]; onChange: (v: ShareDraft[]) => void; contacts: Contact[]; total: number | null; currency: string;
  /** returned or covered per existing share: such a share cannot be removed or shrink below it */
  settled?: Map<number, number>;
}) {
  const set = (key: string, patch: Partial<ShareDraft>) => onChange(value.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const others = sharesTotal(value);
  const mine = total !== null ? total - others : null;
  return (
    <div className="shares">
      {value.map((d) => {
        const locked = (settled?.get(d.debtId ?? 0) ?? 0) > 0.005;
        return (
          <div key={d.key} className="share-row">
            <Select className="share-who" aria-label="Кто должен" value={d.contactId} onChange={(e) => set(d.key, { contactId: e.target.value ? Number(e.target.value) : '' })}>
              <option value="">{d.debtId && !d.contactId ? d.contactName || '—' : 'Новый человек'}</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            {!d.contactId && !d.debtId ? <Input className="input share-name" value={d.contactName} onChange={(e) => set(d.key, { contactName: e.target.value })} placeholder="Имя" aria-label="Имя" /> : null}
            <NumberInput value={d.amount} onChange={(v) => set(d.key, { amount: v })} placeholder="Сумма" aria-label="Его часть" />
            <button type="button" className="icon-btn" aria-label="Убрать" disabled={locked} title={locked ? 'По доле уже был возврат' : undefined}
              onClick={() => onChange(value.filter((x) => x.key !== d.key))}>
              <Icon name="close" size={18} />
            </button>
          </div>
        );
      })}
      <div className="share-foot">
        <Button size="sm" onClick={() => onChange([...value, { key: crypto.randomUUID(), contactId: '', contactName: '', amount: null }])}>
          <Icon name="plus" size={16} /> Человек
        </Button>
        {others > 0 && mine !== null ? (
          <span className={cx('small', mine < 0 ? 'neg' : 'muted')}>Ваша часть <span className="money">{money(mine, currency)}</span></span>
        ) : null}
      </div>
    </div>
  );
}

function shareStatus(s: Share, currency: string) {
  if (s.remaining <= 0.005) return s.covered > 0.005 ? 'оплачено вами' : 'вернул';
  if (s.paid > 0.005 || s.covered > 0.005) return `осталось ${money(s.remaining, currency)}`;
  return 'ждёт возврата';
}

/** Shares of a saved operation: who returned, «Оплатить за него», changing the list. */
export function SharesPanel({ transactionId, total, currency, contacts }: { transactionId: number; total: number; currency: string; contacts: Contact[] }) {
  const key = `/transactions/${transactionId}/shares`;
  const q = useQuery<SharedOperation | null>(key);
  const [edit, setEdit] = useState<ShareDraft[] | null>(null);
  const { busy, run } = useAction();
  const op = q.data;
  const shares = op?.shares ?? [];
  const refresh = () => { invalidate(key); invalidateMoney(); };
  const save = () => run(async () => {
    await put(key, { shares: sharesOf(edit ?? []) });
    refresh();
    setEdit(null);
    toast('Доли сохранены');
  });
  const cover = (s: Share) => run(async () => {
    await post(`/shares/${s.debtId}/cover`);
    refresh();
    toast(`${money(s.remaining, currency)} за ${s.contactName ?? 'человека'} — теперь ваша трата`);
  });

  if (edit) {
    return (
      <div className="shares-panel">
        <div className="shares-head"><span className="qa-lines-label">Доли других</span></div>
        <SharesEditor value={edit} onChange={setEdit} contacts={contacts} total={total} currency={currency}
          settled={new Map(shares.map((s) => [s.debtId, s.paid + s.covered]))} />
        <div className="row-gap share-actions">
          <Button size="sm" onClick={() => setEdit(null)}>Отмена</Button>
          <Button size="sm" variant="primary" busy={busy} onClick={save}>Сохранить доли</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="shares-panel">
      <div className="shares-head">
        <span className="qa-lines-label">Доли других</span>
        <button type="button" className="link-like small" onClick={() => setEdit(shareDrafts(shares))}>{shares.length ? 'Изменить' : 'Добавить'}</button>
      </div>
      {shares.map((s) => (
        <div key={s.debtId} className="share-line">
          <span className="grow">{s.contactName ?? '—'}<span className="muted small"> · {shareStatus(s, currency)}</span></span>
          <span className="money">{money(s.amount, currency)}</span>
          {s.remaining > 0.005 ? (
            <ConfirmButton onConfirm={() => cover(s)} confirmText={`Списать ${money(s.remaining, currency)} на себя?`}>Оплатить за него</ConfirmButton>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** «Возврат доли»: an open shared operation, then the person who returned their part. */
export function ShareReturnPicker({ debtId, onPick }: { debtId: number | ''; onPick: (op: SharedOperation, share: Share | null) => void }) {
  const q = useQuery<SharedOperation[]>('/shared?open=1');
  const ops = q.data ?? [];
  // a plan line not paid yet collects its shares too; a paid one is its operation
  const keyOf = (o: SharedOperation) => (o.transactionId ? `t${o.transactionId}` : `l${o.periodItemId}`);
  const chosen = ops.find((o) => o.shares.some((s) => s.debtId === debtId));
  const [opKey, setOpKey] = useState<string | null>(chosen ? keyOf(chosen) : null);
  const op = ops.find((o) => keyOf(o) === (opKey ?? (chosen && keyOf(chosen))));
  if (!ops.length) return null;
  return (
    <div className="qa-lines">
      <span className="qa-lines-label">Возврат доли</span>
      <div className="chips">
        {ops.map((o) => (
          <button key={keyOf(o)} type="button" className={cx('chip-btn', op && keyOf(op) === keyOf(o) && 'on')} aria-pressed={!!op && keyOf(op) === keyOf(o)}
            onClick={() => { const off = !!op && keyOf(op) === keyOf(o); setOpKey(off ? null : keyOf(o)); if (off) onPick(o, null); }}>
            <span>{o.note || o.categoryName}</span><span className="muted">{o.transactionId ? dateShort(o.date) : `план с ${dateShort(o.date)}`}</span>
          </button>
        ))}
      </div>
      {op ? (
        <div className="chips">
          {op.shares.filter((s) => s.remaining > 0.005 || s.debtId === debtId).map((s) => (
            <button key={s.debtId} type="button" className={cx('chip-btn', debtId === s.debtId && 'on')} aria-pressed={debtId === s.debtId} onClick={() => onPick(op, s)}>
              <span>{s.contactName ?? '—'}</span><span className="money muted">{money(s.remaining, op.currency)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
